<template>
  <div class="journey-responses">
    <page-hero
        class="mb-1"
        :title="msg('journeyresponses')"
        :subtitle="msg('journeyresponsesintro')"
        variant="client"
        :stats="[{ icon: 'InboxIcon', value: page.total, label: msg('responses') }]"
    />

    <b-card no-body class="jr-card">
      <div class="jr-filters">
        <b-form-input v-model="filter.search" class="jr-filters__search" :placeholder="msg('Search')"
                      @change="refresh"/>
        <v-select
            append-to-body class="jr-filters__sel"
            v-model="filter.journeyId" :options="journeys" label="name"
            :reduce="journey => journey.id" :placeholder="msg('guestjourney')"
            :dir="isRTL ? 'rtl' : 'ltr'" @input="refresh"
        />
        <v-select
            append-to-body class="jr-filters__sel"
            v-model="filter.status" :options="statusOptions" label="text"
            :reduce="option => option.value" :placeholder="msg('responsestatus')"
            :dir="isRTL ? 'rtl' : 'ltr'" @input="refresh"
        />
        <b-button variant="flat-secondary" size="sm" @click="refresh">
          <feather-icon icon="RefreshCwIcon" size="14"/>
        </b-button>
      </div>

      <div v-if="loading" class="text-center text-primary my-3">
        <b-spinner class="align-middle mx-1"/>
        <strong>{{ msg('Loading') }}...</strong>
      </div>

      <div v-else-if="responses.length === 0" class="jr-empty">
        <feather-icon icon="InboxIcon" size="30" class="mb-50"/>
        <div>{{ msg('noresponsesyet') }}</div>
      </div>

      <b-table
          v-else
          class="jr-table"
          :items="responses"
          :fields="fields"
          hover responsive small
          primary-key="id"
          :tbody-tr-class="rowClass"
          @row-clicked="open"
      >
        <template #cell(clientName)="data">
          <div class="font-weight-bold">{{ data.item.clientName || '—' }}</div>
          <small class="text-muted">{{ data.item.propertyAddress }}</small>
        </template>
        <template #cell(journeyName)="data">
          {{ data.item.journeyName }}
        </template>
        <template #cell(status)="data">
          <b-badge :variant="data.item.status === 'SUBMITTED' ? 'light-success' : 'light-warning'">
            {{ msg(data.item.status === 'SUBMITTED' ? 'submitted' : 'draft') }}
          </b-badge>
        </template>
        <template #cell(upsell)="data">
          <span v-if="data.item.totalUpsellValue">
            {{ data.item.totalUpsellValue }} {{ data.item.currency || '€' }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(submittedAt)="data">
          <span v-if="data.item.submittedAt">
            {{ $moment(data.item.submittedAt, 'YYYY-MM-DD HH:mm').format('DD/MM/YY HH:mm') }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(actions)="data">
          <b-button variant="flat-primary" size="sm" class="btn-icon" @click.stop="open(data.item)">
            <feather-icon icon="EyeIcon" size="14"/>
          </b-button>
          <b-button variant="flat-danger" size="sm" class="btn-icon" @click.stop="confirmDelete(data.item)">
            <feather-icon icon="Trash2Icon" size="14"/>
          </b-button>
        </template>
      </b-table>

      <div class="jr-pagination">
        <b-pagination
            v-model="page.currentPage" :total-rows="page.total" :per-page="page.amount"
            first-number last-number @input="refresh"
        />
      </div>
    </b-card>

    <b-modal v-model="dialog" size="lg" centered hide-footer :title="msg('response')">
      <journey-response-detail :response="selected"/>
      <div class="text-right mt-1">
        <b-button v-if="selected && selected.contractId" variant="outline-primary" size="sm"
                  @click="openContract">
          <feather-icon icon="ExternalLinkIcon" size="14" class="mr-25"/>{{ msg('Reservation') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import PageHero from '@/components/PageHero'
import JourneyResponseDetail from '@/components/panels/journey/journeyResponseDetail.vue'

export default {
  name: 'JourneyResponses',
  components: {PageHero, JourneyResponseDetail},
  data() {
    return {
      loading: true,
      responses: [],
      journeys: [],
      selected: null,
      dialog: false,
      filter: {search: '', journeyId: null, status: null},
      page: {currentPage: 1, amount: 25, total: 0},
      listeners: {refresh: () => {}},
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('appConfig', ['isRTL']),
    statusOptions() {
      return [
        {value: 'SUBMITTED', text: this.msg('submitted')},
        {value: 'DRAFT', text: this.msg('draft')},
      ];
    },
    fields() {
      return [
        {key: 'clientName', label: this.msg('Guest')},
        {key: 'journeyName', label: this.msg('guestjourney')},
        {key: 'status', label: this.msg('Status'), thStyle: {width: '110px'}},
        {key: 'upsell', label: this.msg('upsellrevenue'), thStyle: {width: '120px'}},
        {key: 'submittedAt', label: this.msg('submittedon'), thStyle: {width: '140px'}},
        {key: 'actions', label: '', thStyle: {width: '90px'}, tdClass: 'text-right'},
      ];
    },
  },
  created() {
    this.refresh();
    this.loadJourneys();
    this.listeners.refresh = () => this.refresh();
    this.$root.$on('crm::branch-updated', this.listeners.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh);
  },
  methods: {
    refresh() {
      const $this = this;
      this.loading = true;
      const filter = {eq: {}, like: {}};
      if (this.filter.journeyId) filter.eq.journeyId = this.filter.journeyId;
      if (this.filter.status) filter.eq.status = this.filter.status;
      if (this.filter.search) filter.like.clientName = this.filter.search;

      this.$store.dispatch('journey/getResponses', {
        page: this.page.currentPage,
        amount: this.page.amount,
        filter,
      }).then(ans => {
        $this.responses = ans['Responses'] || [];
        $this.page.total = ans['Total'] || 0;
      }).finally(() => {
        $this.loading = false;
        $this.$store.dispatch('journey/getUnread');
      });
    },
    loadJourneys() {
      const $this = this;
      this.$store.dispatch('journey/getAll', {amount: 200})
          .then(ans => $this.journeys = ans['Journeys'] || [])
          .catch(() => {
          });
    },
    rowClass(item) {
      return item && item.read !== true ? 'jr-row--unread' : '';
    },
    open(response) {
      const $this = this;
      this.selected = response;
      this.dialog = true;
      if (response.read !== true) {
        this.$store.dispatch('journey/markRead', response.id).then(() => {
          response.read = true;
          $this.$store.dispatch('journey/getUnread');
        });
      }
    },
    openContract() {
      window.open('/real_estate/contracts/rent/edit/' + this.selected.contractId, '_blank');
    },
    confirmDelete(response) {
      const $this = this;
      this.$bvModal.msgBoxConfirm(this.msg('Are you sure?'), {
        title: this.msg('Confirm deletion'),
        okVariant: 'danger',
        okTitle: this.msg('Yes, Delete'),
        cancelTitle: this.msg('cancel'),
        centered: true,
      }).then(confirmed => {
        if (confirmed !== true) return;
        $this.$store.dispatch('journey/removeResponse', response.id).then(() => $this.refresh());
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.jr-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(34, 41, 47, .08);

  &__search {
    width: 220px;
  }

  &__sel {
    min-width: 190px;
  }
}

.jr-empty {
  text-align: center;
  color: #b9b9c3;
  padding: 3rem 1rem;
}

.jr-table {
  margin-bottom: 0;

  ::v-deep tbody tr {
    cursor: pointer;
  }

  ::v-deep .jr-row--unread td {
    font-weight: 600;
    background: rgba(115, 103, 240, .04);
  }
}

.jr-pagination {
  display: flex;
  justify-content: flex-end;
  padding: 10px 18px;

  ::v-deep .pagination {
    margin: 0;
  }
}

.dark-layout {
  .jr-filters {
    border-bottom-color: rgba(255, 255, 255, .08);
  }

  .jr-table ::v-deep .jr-row--unread td {
    background: rgba(115, 103, 240, .08);
  }
}
</style>
